import { useT, useUiStore } from '@/store';
import { usePendingRequestCount } from '@/hooks/useClientRequests';
import { useNotifications } from '@/hooks/useNotifications';

/**
 * Thumb-reachable navigation for a dispatcher holding a phone.
 *
 * Four destinations, not the console's nine. The top tab strip wraps to
 * three rows on a 390px screen and puts every target at the far end of a
 * thumb's arc; this is the small set that matters when someone is standing
 * in a yard rather than sitting at a desk — where the trucks are, what is
 * moving, what is waiting on them, and what has gone wrong.
 *
 * The other five views are still reachable: the top strip is scrollable
 * rather than hidden on mobile (see Header), so this is the fast path, not
 * a smaller product.
 *
 * `md:hidden` — on a desktop the top strip is already thumb-irrelevant and
 * a fixed bottom bar would just eat vertical space.
 */
const TABS = [
  { id: 'dispatcher', labelKey: 'bottomNav.map', glyph: '🗺' },
  { id: 'orders', labelKey: 'bottomNav.orders', glyph: '📋' },
  { id: 'approvals', labelKey: 'bottomNav.approvals', glyph: '✅', badge: 'approvals' },
  { id: 'alerts', labelKey: 'bottomNav.alerts', glyph: '🔔', badge: 'alerts' },
];

export function BottomNav() {
  const t = useT();
  const view = useUiStore((s) => s.view);
  const setView = useUiStore((s) => s.setView);
  const pendingRequests = usePendingRequestCount();
  const { unreadCount } = useNotifications();

  const badgeFor = (kind) => {
    if (kind === 'approvals') return pendingRequests;
    if (kind === 'alerts') return unreadCount;
    return 0;
  };

  return (
    <nav
      aria-label={t('bottomNav.label')}
      // pb-[env(safe-area-inset-bottom)]: on an iPhone the home indicator
      // sits over the last ~34px, and without this the buttons are under it.
      className="fixed inset-x-0 bottom-0 z-[200] flex border-t border-line bg-bg/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-[10px] md:hidden"
    >
      {TABS.map((tab) => {
        const active = view === tab.id;
        const count = badgeFor(tab.badge);
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => setView(tab.id)}
            aria-current={active ? 'page' : undefined}
            // min-h-14: a 44px touch target is the smallest that a thumb hits
            // reliably; this clears it with room for the label.
            className={`relative flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 px-1 py-2 text-[10px] font-semibold transition-colors ${
              active ? 'text-accent' : 'text-dim'
            }`}
          >
            <span aria-hidden="true" className="text-[17px] leading-none">
              {tab.glyph}
            </span>
            <span className="leading-none">{t(tab.labelKey)}</span>
            {count > 0 ? (
              <span
                className="absolute right-[18%] top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red px-1 text-[9px] font-bold text-white"
                aria-label={t('bottomNav.pending', { n: count })}
              >
                {count > 9 ? '9+' : count}
              </span>
            ) : null}
            {active ? (
              <span aria-hidden="true" className="absolute inset-x-4 top-0 h-0.5 rounded-full bg-accent" />
            ) : null}
          </button>
        );
      })}
    </nav>
  );
}
